import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';

export interface TranslationDictionary {
  [key: string]: string;
}

export interface TranslationApiItem {
  SYS_PlatformType_ID: number;
  CategoryID: number;
  Key: string;
  Phrase: string;
  TransText: string;
}

export interface TranslationApiResponse {
  RESULT?: string;
  List?: TranslationApiItem[];
}

export const PAGE_CODE_MAP = {
  common: [1],
  authManage: [1, 2],
  eos: [1, 3],
  posOrder: [1, 4],
  product: [1, 5],
  report: [1, 6],
  storeManage: [1, 7],
  sys: [1, 8],
  user: [1, 9],
};

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly translationKeyAliases: { [key: string]: string[] } = {
    report_sales_quantity_ratio_rank: ['common_sales_quantity_ratio_ranking'],
    report_sales_amount_ratio_rank: ['common_sales_amount_ratio_ranking'],
    common_sales_quantity_ratio_ranking: ['report_sales_quantity_ratio_rank'],
    common_sales_amount_ratio_ranking: ['report_sales_amount_ratio_rank'],
  };
  private readonly cacheVersion = '20260528001';
  private readonly translationCachePrefix = 'translations_';
  private readonly storageLanguageKey = 'languageCode';
  private readonly defaultLanguage = 'zh-TW';
  private readonly apiUrl = '/api/TranslationSetting/GetTranslationList';
  private readonly platformTypeId = 1;

  private currentLanguageSubject = new BehaviorSubject<string>(this.defaultLanguage);
  private translationsSubject = new BehaviorSubject<TranslationDictionary>({});
  private languageChangedSubject = new Subject<string>();

  private memoryCache: { [cacheKey: string]: TranslationDictionary } = {};
  private currentPageCodes: number[] = [];

  currentLanguage$ = this.currentLanguageSubject.asObservable();
  translations$ = this.translationsSubject.asObservable();
  languageChanged$ = this.languageChangedSubject.asObservable();

  constructor(private http: HttpClient) {
    this.clearOldTranslationCache();
  }

  initLanguage(): string {
    const savedLanguage = localStorage.getItem(this.storageLanguageKey);
    const resolvedLanguage =
      savedLanguage && savedLanguage.trim() ? savedLanguage : this.defaultLanguage;

    if (!savedLanguage || !savedLanguage.trim()) {
      localStorage.setItem(this.storageLanguageKey, resolvedLanguage);
    }

    this.currentLanguageSubject.next(resolvedLanguage);
    return resolvedLanguage;
  }

  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  getDefaultLanguage(): string {
    return this.defaultLanguage;
  }

  async setLanguage(languageCode: string, pageCodes?: number[]): Promise<void> {
    this.currentLanguageSubject.next(languageCode);
    localStorage.setItem(this.storageLanguageKey, languageCode);

    if (pageCodes?.length) {
      this.currentPageCodes = [...pageCodes];
      await this.loadTranslations(pageCodes);
    }

    this.languageChangedSubject.next(languageCode);
  }

  setCurrentPageCodes(pageCodes: number[]): void {
    this.currentPageCodes = [...pageCodes];
  }

  getCurrentPageCodes(): number[] {
    return [...this.currentPageCodes];
  }

  async reloadCurrentPageTranslations(): Promise<void> {
    if (!this.currentPageCodes.length) {
      return;
    }

    await this.loadTranslations(this.currentPageCodes);
    this.languageChangedSubject.next(this.getCurrentLanguage());
  }

  async loadTranslations(pageCodes: number[]): Promise<TranslationDictionary> {
    const normalizedPageCodes = [...new Set(pageCodes)].sort((a, b) => a - b);
    this.currentPageCodes = normalizedPageCodes;

    const languageCode = this.getCurrentLanguage() || this.initLanguage();
    const cacheKey = this.buildCacheKey(normalizedPageCodes, languageCode);

    const memoryData = this.getFromMemoryCache(cacheKey);
    if (memoryData) {
      this.translationsSubject.next(memoryData);
      return memoryData;
    }

    const localData = this.getFromLocalStorage(cacheKey);
    if (localData) {
      this.memoryCache[cacheKey] = localData;
      this.translationsSubject.next(localData);
      return localData;
    }

    try {
      const apiData = await this.fetchTranslationsFromApi(normalizedPageCodes, languageCode);
      this.saveToCache(cacheKey, apiData);
      this.translationsSubject.next(apiData);
      return apiData;
    } catch (error) {
      if (languageCode !== this.defaultLanguage) {
        const fallbackData = await this.loadDefaultLanguageTranslations(normalizedPageCodes);
        this.translationsSubject.next(fallbackData);
        return fallbackData;
      }

      throw error;
    }
  }

  async fetchTranslationsFromApi(
    pageCodes: number[],
    languageCode?: string,
  ): Promise<TranslationDictionary> {
    const dictionary: TranslationDictionary = {};
    const targetLanguage = languageCode || this.getCurrentLanguage();

    for (const pageCode of pageCodes) {
      const response = await this.http.post<TranslationApiResponse>(this.apiUrl, {
        SYS_PlatformType_ID: this.platformTypeId,
        CategoryID: pageCode,
        LanguageCode: targetLanguage,
      }).toPromise();

      if (!response?.List?.length) {
        continue;
      }

      response.List.forEach((item: TranslationApiItem) => {
        const finalText = item.TransText && item.TransText.trim()
          ? item.TransText.trim()
          : (item.Phrase || '').trim();

        if (item.Key && finalText) {
          dictionary[item.Key] = finalText;
        }
      });
    }

    return dictionary;
  }

  buildCacheKey(pageCodes: number[], languageCode: string): string {
    return `${this.cacheVersion}_${languageCode}_${pageCodes.join('-')}`;
  }

  getFromMemoryCache(cacheKey: string): TranslationDictionary | null {
    return this.memoryCache[cacheKey] || null;
  }

  getFromLocalStorage(cacheKey: string): TranslationDictionary | null {
    const localKey = `${this.translationCachePrefix}${cacheKey}`;
    const rawData = localStorage.getItem(localKey);

    if (!rawData) {
      return null;
    }

    try {
      return JSON.parse(rawData) as TranslationDictionary;
    } catch {
      return null;
    }
  }

  saveToCache(cacheKey: string, translations: TranslationDictionary): void {
    const localKey = `${this.translationCachePrefix}${cacheKey}`;
    this.memoryCache[cacheKey] = translations;
    localStorage.setItem(localKey, JSON.stringify(translations));
  }

  resetTranslations(): void {
    this.translationsSubject.next({});
  }

  clearMemoryCache(): void {
    this.memoryCache = {};
  }

  hasTranslation(key: string): boolean {
    return !!this.findTranslationText(key);
  }

  getText(key: string, params: { [key: string]: any } = {}): string {
    const text = this.findTranslationText(key) || key;
    return this.replaceParams(text, params);
  }

  mergeTranslations(newTranslations: TranslationDictionary): void {
    const merged = {
      ...this.translationsSubject.value,
      ...newTranslations,
    };

    this.translationsSubject.next(merged);
  }

  private async loadDefaultLanguageTranslations(
    pageCodes: number[],
  ): Promise<TranslationDictionary> {
    const cacheKey = this.buildCacheKey(pageCodes, this.defaultLanguage);

    const memoryData = this.getFromMemoryCache(cacheKey);
    if (memoryData) {
      return memoryData;
    }

    const localData = this.getFromLocalStorage(cacheKey);
    if (localData) {
      this.memoryCache[cacheKey] = localData;
      return localData;
    }

    const fallbackData = await this.fetchTranslationsFromApi(pageCodes, this.defaultLanguage);
    this.saveToCache(cacheKey, fallbackData);
    return fallbackData;
  }

  private replaceParams(
    text: string,
    params: { [key: string]: any },
  ): string {
    let result = text;

    Object.keys(params).forEach((paramKey) => {
      result = result.replace(`{${paramKey}}`, String(params[paramKey]));
    });

    return result;
  }

  private findTranslationText(key: string): string | null {
    const translations = this.translationsSubject.value;

    if (translations[key]) {
      return translations[key];
    }

    const aliases = this.translationKeyAliases[key] || [];
    for (const aliasKey of aliases) {
      if (translations[aliasKey]) {
        return translations[aliasKey];
      }
    }

    return null;
  }

  private clearOldTranslationCache(): void {
    const currentVersionPrefix = `${this.translationCachePrefix}${this.cacheVersion}_`;

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(this.translationCachePrefix)) {
        continue;
      }

      if (!key.startsWith(currentVersionPrefix)) {
        localStorage.removeItem(key);
      }
    }
  }
}
